import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Snackbar } from 'Components';
import { Pathname } from 'Routes';
import { baseUrl, getToken, axios as apiAxios } from 'Api';
import { getVideosDetail } from 'Api/apiFunctions';
import image01 from 'Assets/birthday.png';
import image04 from 'Assets/noThumbImage.png';
import styles from './index.module.css';

export const GiftContent = () => {
  const navigate = useNavigate();

  const receiverName = [
    localStorage.getItem('firstname') || '',
    localStorage.getItem('lastname') || '',
  ].filter(Boolean).join(' ') || 'Your';

  // Snackbar
  const [snackbar, setSnackbar] = useState({ isVisible: false, message: '', type: 'success' });

  // Content tab state
  const [contentTabType, setContentTabType] = useState('videos');
  const [contentLoading, setContentLoading] = useState(false);

  // Content data from API
  const [userVideos, setUserVideos] = useState([]);
  const [userAlbums, setUserAlbums] = useState([]);

  // Soft-deleted items waiting for restore or permanent deletion
  const [deletedItems, setDeletedItems] = useState([]); // { type: 'video'|'album', ...itemData }

  // Category folder state
  const [selectedCategory, setSelectedCategory] = useState(null);
  // Inner tab when inside a folder: 'all', 'videos', 'photos'
  const [folderTab, setFolderTab] = useState('all');

  // Derive unique categories from video data
  const categories = useMemo(() => {
    const catMap = {};
    userVideos.forEach((video) => {
      const cat = video.category || 'All Videos';
      if (!catMap[cat]) {
        catMap[cat] = { name: cat, count: 0, thumbnail: video.thumbnail };
      }
      catMap[cat].count += 1;
    });
    return Object.values(catMap);
  }, [userVideos]);

  // Filtered videos for selected category
  const filteredVideos = useMemo(() => {
    if (!selectedCategory) return userVideos;
    return userVideos.filter((v) => (v.category || 'All Videos') === selectedCategory);
  }, [userVideos, selectedCategory]);

  // Filtered albums for selected category — match by contributor name from title or category
  const filteredAlbums = useMemo(() => {
    if (!selectedCategory) return userAlbums;
    const name = selectedCategory.replace(/'s\s+\w+$/i, '').trim().toLowerCase();
    return userAlbums.filter((a) => {
      const fromTitle = (a.title || '').replace(/'s\s+\w+$/i, '').trim().toLowerCase();
      const fromCategory = (a.category || '').replace(/'s\s+\w+$/i, '').trim().toLowerCase();
      return fromTitle === name || fromCategory === name;
    });
  }, [userAlbums, selectedCategory]);

  // Video preview modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);

  // Album photos modal
  const [albumOpen, setAlbumOpen] = useState(false);
  const [albumLoading, setAlbumLoading] = useState(false);
  const [albumDetail, setAlbumDetail] = useState(null);

  // Fetch all content (all pages) using same API as YourVideos
  const fetchContent = useCallback(async () => {
    if (!getToken()) return;
    try {
      let allVideos = [];
      let allAlbums = [];
      let currentPage = 1;
      let lastPage = 1;

      // Loop through all pages to fetch everything
      do {
        // eslint-disable-next-line no-await-in-loop
        const response = await apiAxios.post(`${baseUrl}/V1/getMyContentsBasedOnUploadDate`, {
          page: currentPage,
        });

        if (response.data?.success && response.data?.data) {
          const { data, paginate } = response.data;
          allVideos = [...allVideos, ...(data.video || [])];
          allAlbums = [...allAlbums, ...(data.album || [])];

          if (paginate?.last_page) {
            lastPage = paginate.last_page;
          } else if (paginate?.total_pages) {
            lastPage = paginate.total_pages;
          }
        } else {
          break;
        }
        currentPage += 1;
      } while (currentPage <= lastPage);

      // Map videos
      const videos = allVideos.map((video) => {
        const videoThumbnail = Array.isArray(video.thumbnail)
          ? video.thumbnail[0] || image04
          : video.thumbnail || video.thumbnails || image04;
        return {
          id: video.content_id || video.video_id || video.id,
          title: video.title || 'Untitled',
          thumbnail: videoThumbnail,
          duration: video.duration || video.video_duration || '',
          date: video.publish_date || video.created || '',
          category: video.category_name || video.category || 'All Videos',
        };
      });
      setUserVideos(videos);

      // Map albums
      const albums = allAlbums.map((album) => {
        const albumThumbnail = Array.isArray(album.thumbnail)
          ? album.thumbnail[0] || image01
          : album.thumbnail || album.thumbnails || image01;
        return {
          id: album.content_id || album.id || album.album_id,
          title: album.title || album.album_title || 'Untitled Album',
          thumbnail: albumThumbnail,
          photoCount: album.photo_count || album.total_photos || (Array.isArray(album.photos) ? album.photos.length : 0),
          date: album.created_at || album.upload_date || album.created || '',
          category: album.category_name || album.category || 'All Videos',
        };
      });
      setUserAlbums(albums);
    } catch (err) {
      console.error('[GIFT_CONTENT] Error fetching content:', err);
      setSnackbar({ isVisible: true, message: 'Failed to load content', type: 'error' });
    }
  }, []);

  // Open video preview modal - fetch video details to get video_raw_file
  const handleVideoClick = async (video) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewVideo({ title: video.title, thumbnail: video.thumbnail });
    try {
      const response = await getVideosDetail({ id: video.id });
      if (response?.data?.success && response?.data?.data) {
        const details = response.data.data;
        const videoUrl = details.video_raw_file || '';
        setPreviewVideo({
          title: details.title || video.title,
          thumbnail: video.thumbnail,
          videoUrl,
        });
      }
    } catch (err) {
      console.error('[GIFT_CONTENT] Error fetching video details:', err);
      setSnackbar({ isVisible: true, message: 'Failed to load video preview', type: 'error' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewVideo(null);
  };

  // Open album photos modal - fetch album details using same API as main app
  const handleAlbumClick = async (album) => {
    setAlbumOpen(true);
    setAlbumLoading(true);
    setAlbumDetail({ title: album.title, thumbnail: album.thumbnail, photos: [], albumId: album.id });
    try {
      const response = await apiAxios.post(`${baseUrl}/getMyAlbumDetail`, {
        album_id: album.id,
      });
      if (response?.data?.success && response?.data?.data) {
        const { data } = response.data;
        const photos = data[0]?.photos || data?.photos || [];
        const deletedPhotoIds = new Set(deletedItems.filter((d) => d.type === 'photo').map((d) => String(d.id)));
        setAlbumDetail({
          title: data[0]?.title || album.title,
          thumbnail: album.thumbnail,
          photos: photos.filter((p) => !deletedPhotoIds.has(String(p.id))),
          albumId: album.id,
        });
      }
    } catch (err) {
      console.error('[GIFT_CONTENT] Error fetching album details:', err);
      setSnackbar({ isVisible: true, message: 'Failed to load album photos', type: 'error' });
    } finally {
      setAlbumLoading(false);
    }
  };

  const closeAlbum = () => {
    setAlbumOpen(false);
    setAlbumDetail(null);
  };

  // Soft delete — move item to Restore tab instead of calling API
  const handleSoftDelete = (type, item, e) => {
    e.stopPropagation();
    if (type === 'video') {
      setUserVideos((prev) => prev.filter((v) => v.id !== item.id));
    } else {
      setUserAlbums((prev) => prev.filter((a) => a.id !== item.id));
    }
    setDeletedItems((prev) => [...prev, { ...item, type }]);
    setSnackbar({ isVisible: true, message: 'Moved to Restore tab', type: 'success' });
  };

  // Restore — move item back from Restore tab to its original list
  const handleRestore = (item) => {
    setDeletedItems((prev) => prev.filter((d) => !(d.id === item.id && d.type === item.type)));
    if (item.type === 'video') {
      setUserVideos((prev) => [...prev, item]);
      setSnackbar({ isVisible: true, message: 'Video restored', type: 'success' });
    } else if (item.type === 'photo') {
      // Restore photo back into its album
      setUserAlbums((prev) => prev.map((a) => {
        if (String(a.id) === String(item.albumId)) {
          return { ...a, photoCount: (a.photoCount || 0) + 1 };
        }
        return a;
      }));
      // Also restore into album detail modal if it's currently open
      if (albumDetail && String(albumDetail.albumId) === String(item.albumId)) {
        setAlbumDetail((prev) => ({
          ...prev,
          photos: [...(prev.photos || []), item.photoData],
        }));
      }
      setSnackbar({ isVisible: true, message: 'Photo restored', type: 'success' });
    } else {
      setUserAlbums((prev) => [...prev, item]);
      setSnackbar({ isVisible: true, message: 'Album restored', type: 'success' });
    }
  };

  // Permanently delete all items in Restore tab via API, then navigate
  const handleNextClick = async () => {
    if (deletedItems.length > 0) {
      // Fire all delete API calls in parallel, don't block navigation
      const deletePromises = deletedItems.map((item) => {
        if (item.type === 'video') {
          return apiAxios.post(`${baseUrl}/deleteVideo`, { video_id: item.id }).catch((err) => {
            console.error('[GIFT_CONTENT] Failed to delete video:', item.id, err);
          });
        }
        if (item.type === 'photo') {
          return apiAxios.post(`${baseUrl}/deleteMyPhoto`, { photo_id: item.id }).catch((err) => {
            console.error('[GIFT_CONTENT] Failed to delete photo:', item.id, err);
          });
        }
        return apiAxios.post(`${baseUrl}/deleteMyAlbum`, { album_id: item.id }).catch((err) => {
          console.error('[GIFT_CONTENT] Failed to delete album:', item.id, err);
        });
      });
      // Don't await — fire and navigate
      Promise.all(deletePromises);
    }
    navigate(`${Pathname.authentication.ecosystem}?fromGift=true`, { state: { isGiftFlow: true } });
  };

  // Fetch all content on mount
  useEffect(() => {
    if (!getToken()) return;
    setContentLoading(true);
    fetchContent().finally(() => setContentLoading(false));
  }, [fetchContent]);

  // If restore tab becomes empty, switch back to videos
  useEffect(() => {
    if (contentTabType === 'restore' && deletedItems.length === 0) {
      setContentTabType('videos');
    }
  }, [deletedItems.length, contentTabType]);

  return (
    <div className={styles.giftContentPage}>
      <Snackbar
        isVisible={snackbar.isVisible}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar({ ...snackbar, isVisible: false })}
      />

      {/* Video Preview Modal */}
      {previewOpen && (
        <div role="button" tabIndex={0} className={styles.previewOverlay} onClick={closePreview} onKeyDown={(e) => { if (e.key === 'Escape') closePreview(); }}>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div role="dialog" aria-modal="true" className={styles.previewModal} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <button type="button" className={styles.previewClose} onClick={closePreview}>
              &times;
            </button>
            <div className={styles.previewPlayer}>
              {previewLoading ? (
                <div className={styles.previewLoading}>
                  <div className={styles.loadingSpinner} />
                  Loading preview...
                </div>
              ) : previewVideo?.videoUrl ? (
                <video
                  src={previewVideo.videoUrl}
                  controls
                  muted
                  className={styles.previewVideoElement}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className={styles.previewLoading}>
                  <img
                    src={previewVideo?.thumbnail || image04}
                    alt={previewVideo?.title}
                    className={styles.previewFallbackImg}
                  />
                  <p>Video preview not available</p>
                </div>
              )}
            </div>
            {previewVideo?.title && (
              <div className={styles.previewTitle}>{previewVideo.title}</div>
            )}
          </div>
        </div>
      )}

      {/* Album Photos Modal */}
      {albumOpen && (
        <div role="button" tabIndex={0} className={styles.previewOverlay} onClick={closeAlbum} onKeyDown={(e) => { if (e.key === 'Escape') closeAlbum(); }}>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div role="dialog" aria-modal="true" className={styles.albumModal} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <button type="button" className={styles.previewClose} onClick={closeAlbum}>
              &times;
            </button>
            {albumDetail?.title && (
              <div className={styles.albumModalHeader}>{albumDetail.title}</div>
            )}
            <div className={styles.albumPhotosContainer}>
              {albumLoading ? (
                <div className={styles.previewLoading}>
                  <div className={styles.loadingSpinner} />
                  Loading photos...
                </div>
              ) : albumDetail?.photos?.length > 0 ? (
                <div className={styles.albumPhotosGrid}>
                  {albumDetail.photos.map((photo, index) => (
                    <div key={photo.id || index} className={styles.albumPhotoItem}>
                      <img
                        src={photo.imagekit_url || photo.photo_file || image01}
                        alt={photo.title || `Photo ${index + 1}`}
                        onError={(e) => { e.target.src = image01; }}
                      />
                      <button
                        type="button"
                        className={styles.albumPhotoDelete}
                        onClick={() => {
                          const photoData = albumDetail?.photos?.find((p) => String(p.id) === String(photo.id));
                          if (photoData) {
                            setAlbumDetail((prev) => {
                              if (!prev) return prev;
                              return { ...prev, photos: prev.photos.filter((p) => String(p.id) !== String(photo.id)) };
                            });
                            setUserAlbums((prev) => prev.map((a) => {
                              if (String(a.id) === String(albumDetail.albumId)) {
                                return { ...a, photoCount: (a.photoCount || 1) - 1 };
                              }
                              return a;
                            }));
                            setDeletedItems((prev) => [...prev, {
                              type: 'photo',
                              id: photoData.id,
                              title: photoData.title || photoData.name || `Photo ${photoData.id}`,
                              thumbnail: photoData.imagekit_url || photoData.photo_file || image01,
                              albumId: albumDetail.albumId,
                              albumTitle: albumDetail?.title || 'Album',
                              photoData,
                            }]);
                            setSnackbar({ isVisible: true, message: 'Moved to Restore tab', type: 'success' });
                          }
                        }}
                        title="Delete photo"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.previewLoading}>
                  <p>No photos in this album</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className={styles.header}>
        <div />
        <div className={styles.headerRight} />
      </div>

      {/* Content Area */}
      <div className={styles.contentWrapper}>
        {/* <h1 className={styles.pageTitle}>{`${receiverName}'s Shared Content`}</h1> */}
        <h1 className={styles.pageTitle}>Your Projector</h1>

        <div className={styles.contentTabContent}>
          {/* Content Type Tabs */}
          <div className={styles.contentTypeTabsRow}>
            {selectedCategory && (
              <button
                type="button"
                className={styles.tabBackButton}
                onClick={() => { setSelectedCategory(null); setFolderTab('all'); }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 12H5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M12 19L5 12L12 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            <div className={styles.contentTypeTabs}>
              {selectedCategory ? (
                <>
                  <button
                    className={`${styles.contentTypeTab} ${folderTab === 'all' ? styles.contentTypeTabActive : ''}`}
                    onClick={() => setFolderTab('all')}
                  >
                    All
                  </button>
                  <button
                    className={`${styles.contentTypeTab} ${folderTab === 'videos' ? styles.contentTypeTabActive : ''}`}
                    onClick={() => setFolderTab('videos')}
                  >
                    Videos
                  </button>
                  <button
                    className={`${styles.contentTypeTab} ${folderTab === 'photos' ? styles.contentTypeTabActive : ''}`}
                    onClick={() => setFolderTab('photos')}
                  >
                    Photo Albums
                  </button>
                </>
              ) : (
                <>
                  <button
                    className={`${styles.contentTypeTab} ${contentTabType === 'videos' ? styles.contentTypeTabActive : ''}`}
                    onClick={() => setContentTabType('videos')}
                  >
                    Folders
                  </button>
                  <button
                    className={`${styles.contentTypeTab} ${contentTabType === 'allVideos' ? styles.contentTypeTabActive : ''}`}
                    onClick={() => setContentTabType('allVideos')}
                  >
                    Videos
                  </button>
                  <button
                    className={`${styles.contentTypeTab} ${contentTabType === 'photos' ? styles.contentTypeTabActive : ''}`}
                    onClick={() => setContentTabType('photos')}
                  >
                    Photo Albums
                  </button>
                  {deletedItems.length > 0 && (
                    <button
                      className={`${styles.contentTypeTab} ${styles.restoreTab} ${contentTabType === 'restore' ? styles.contentTypeTabActive : ''}`}
                      onClick={() => setContentTabType('restore')}
                    >
                      Restore
                      <span className={styles.restoreBadge}>{deletedItems.length}</span>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Action Bar */}
          <div className={styles.contentActionBar}>
            <span className={styles.contentCount}>
              {contentTabType === 'videos' && (
                selectedCategory
                  ? (folderTab === 'videos' ? `${filteredVideos.length} Videos`
                    : folderTab === 'photos' ? `${filteredAlbums.length} Albums`
                      : `${filteredVideos.length + filteredAlbums.length} Items`)
                  : `${categories.length} ${categories.length === 1 ? 'Folder' : 'Folders'}`
              )}
              {contentTabType === 'allVideos' && `${userVideos.length} Videos`}
              {contentTabType === 'photos' && `${userAlbums.length} Albums`}
              {contentTabType === 'restore' && `${deletedItems.length} Items`}
            </span>
          </div>

          {/* Loading State */}
          {contentLoading && (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner} />
              Loading content...
            </div>
          )}

          {/* Content Grid */}
          {!contentLoading && (
            <div className={styles.contentGrid}>
              {contentTabType === 'videos' ? (
                (userVideos.length > 0 || userAlbums.length > 0) ? (
                  selectedCategory ? (
                    /* Inside folder: All / Videos / Photo Albums tabs */
                    <>
                      {(folderTab === 'all' || folderTab === 'videos') && filteredVideos.map((video) => (
                        <div key={video.id} className={styles.contentCard}>
                          <div className={styles.contentCardThumbnail}>
                            <img src={video.thumbnail} alt={video.title} onError={(e) => { e.target.src = image04; }} />
                            {video.duration && <span className={styles.videoDuration}>{video.duration}</span>}
                            <button type="button" className={styles.videoPlayOverlay} onClick={() => handleVideoClick(video)} title="Play Video">
                              <span className={styles.videoPlayIcon}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="#000" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              </span>
                            </button>
                            <button type="button" className={styles.deleteCardButton} onClick={(e) => handleSoftDelete('video', video, e)} title="Delete">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </div>
                          <div className={styles.contentCardInfo}>
                            <div className={styles.contentCardTitleRow}>
                              <h4 className={styles.contentCardTitle}>{video.title}</h4>
                              <span className={styles.contentTypeIcon}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polygon points="23 7 16 12 23 17 23 7" />
                                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                                </svg>
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                      {(folderTab === 'all' || folderTab === 'photos') && filteredAlbums.map((album) => (
                        <div key={`album-${album.id}`} className={styles.contentCard}>
                          <div className={styles.contentCardThumbnail}>
                            <img src={album.thumbnail} alt={album.title} onError={(e) => { e.target.src = image01; }} />
                            <button type="button" className={styles.previewEyeButton} onClick={() => handleAlbumClick(album)} title="Preview">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <circle cx="12" cy="12" r="3" stroke="white" strokeWidth="2" />
                              </svg>
                            </button>
                            <button type="button" className={styles.deleteCardButton} onClick={(e) => handleSoftDelete('album', album, e)} title="Delete">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </div>
                          <div className={styles.contentCardInfo}>
                            <div className={styles.contentCardTitleRow}>
                              <h4 className={styles.contentCardTitle}>{album.title}</h4>
                              <span className={styles.contentTypeIcon}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                  <circle cx="8.5" cy="8.5" r="1.5" />
                                  <polyline points="21 15 16 10 5 21" />
                                </svg>
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                      {folderTab === 'videos' && filteredVideos.length === 0 && (
                        <div className={styles.emptyState}><p>No videos</p></div>
                      )}
                      {folderTab === 'photos' && filteredAlbums.length === 0 && (
                        <div className={styles.emptyState}><p>No photo albums</p></div>
                      )}
                      {folderTab === 'all' && filteredVideos.length === 0 && filteredAlbums.length === 0 && (
                        <div className={styles.emptyState}><p>No content</p></div>
                      )}
                    </>
                  ) : (
                    /* Category folders view */
                    categories.map((cat) => (
                      <div
                        key={cat.name}
                        className={styles.categoryFolder}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedCategory(cat.name)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedCategory(cat.name); }}
                      >
                        <div className={styles.categoryFolderThumbnail}>
                          <img
                            src={cat.thumbnail}
                            alt={cat.name}
                            onError={(e) => { e.target.src = image04; }}
                          />
                          <div className={styles.categoryFolderOverlay}>
                            <svg className={styles.categoryFolderIcon} width="32" height="32" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                              <path d="M10 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V8C22 6.9 21.1 6 20 6H12L10 4Z" />
                            </svg>
                          </div>
                        </div>
                        <div className={styles.categoryFolderInfo}>
                          <h4 className={styles.categoryFolderName}>{cat.name.replace(/'s\s*videos$/i, '')}</h4>
                        </div>
                      </div>
                    ))
                  )
                ) : (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyStateIcon}>&#127909;</div>
                    <p>No content yet</p>
                  </div>
                )
              ) : contentTabType === 'allVideos' ? (
                userVideos.length > 0 ? (
                  userVideos.map((video) => (
                    <div
                      key={video.id}
                      className={styles.contentCard}
                    >
                      <div className={styles.contentCardThumbnail}>
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          onError={(e) => { e.target.src = image04; }}
                        />
                        {video.duration && (
                          <span className={styles.videoDuration}>{video.duration}</span>
                        )}
                        <button
                          type="button"
                          className={styles.videoPlayOverlay}
                          onClick={() => handleVideoClick(video)}
                          title="Play Video"
                        >
                          <span className={styles.videoPlayIcon}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="#000" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </span>
                        </button>
                        <button
                          type="button"
                          className={styles.deleteCardButton}
                          onClick={(e) => handleSoftDelete('video', video, e)}
                          title="Delete"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                      <div className={styles.contentCardInfo}>
                        <div className={styles.contentCardTitleRow}>
                          <h4 className={styles.contentCardTitle}>{video.title}</h4>
                          <span className={styles.contentTypeIcon}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="23 7 16 12 23 17 23 7" />
                              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                            </svg>
                          </span>
                        </div>
                        <div className={styles.contentCardMeta}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{video.category}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyStateIcon}>&#127909;</div>
                    <p>No videos yet</p>
                  </div>
                )
              ) : contentTabType === 'photos' ? (
                userAlbums.length > 0 ? (
                  userAlbums.map((album) => (
                    <div
                      key={album.id}
                      className={styles.contentCard}
                    >
                      <div className={styles.contentCardThumbnail}>
                        <img
                          src={album.thumbnail}
                          alt={album.title}
                          onError={(e) => { e.target.src = image01; }}
                        />
                        <button
                          type="button"
                          className={styles.previewEyeButton}
                          onClick={() => handleAlbumClick(album)}
                          title="Preview"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="12" cy="12" r="3" stroke="white" strokeWidth="2" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className={styles.deleteCardButton}
                          onClick={(e) => handleSoftDelete('album', album, e)}
                          title="Delete"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                      <div className={styles.contentCardInfo}>
                        <div className={styles.contentCardTitleRow}>
                          <h4 className={styles.contentCardTitle}>{album.title}</h4>
                          <span className={styles.contentTypeIcon}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <polyline points="21 15 16 10 5 21" />
                            </svg>
                          </span>
                        </div>
                        <div className={styles.contentCardMeta}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{album.title}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyStateIcon}>&#128247;</div>
                    <p>No photo albums yet</p>
                  </div>
                )
              ) : (
                /* Restore Tab Content */
                deletedItems.length > 0 ? (
                  deletedItems.map((item) => (
                    <div
                      key={`${item.type}-${item.id}`}
                      className={styles.contentCard}
                    >
                      <div className={styles.contentCardThumbnail}>
                        <img
                          src={item.thumbnail}
                          alt={item.title}
                          onError={(e) => { e.target.src = item.type === 'video' ? image04 : image01; }}
                        />
                        <span className={styles.restoreTypeBadge}>
                          {item.type === 'video' ? 'Video' : item.type === 'photo' ? 'Photo' : 'Album'}
                        </span>
                      </div>
                      <div className={styles.contentCardInfo}>
                        <h4 className={styles.contentCardTitle}>{item.title}</h4>
                        <div className={styles.contentCardMeta}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{item.type === 'video' ? item.category : item.type === 'photo' ? `From: ${item.albumTitle}` : item.title}</span>
                        </div>
                        <button
                          type="button"
                          className={styles.restoreCardButton}
                          onClick={(e) => { e.stopPropagation(); handleRestore(item); }}
                          title="Restore"
                        >
                          Restore
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.emptyState}>
                    <p>No items to restore</p>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom centered Next button */}
      <div className={styles.bottomNextContainer}>
        <button
          type="button"
          className={styles.backButton}
          onClick={handleNextClick}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default GiftContent;
